import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

import SwSelect from '../Common/SwReactSelect/SwReactSelect';
import SwInput from '../Common/FormComponent/SwInput';
import AntDDatePicker from '../Common/AntDDateTimePicker/AntDDatePicker';
import { dataUser } from '../Helpers/util';

const userDataLocal = dataUser();
const UserObj = JSON.parse(userDataLocal || '{}');

const AttendanceServiceRequest = forwardRef(({ onClose }, ref) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [numDevices, setNumDevices] = useState('');
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
  });

  const [errors, setErrors] = useState({});

  // ───────────────────────────────
  // Fetch locations
  // ───────────────────────────────
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const authToken = JSON.parse(
          localStorage.getItem('headData') || '{}',
        );

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}basic-data/get-location-by-spoc`,
          {
            params: {
              spoc: UserObj?.rec_id,
              client: UserObj?.company_id,
            },
            headers: {
              Authorization: authToken?.spintlyToken,
            },
          },
        );

        if (res.data.status === 'success') {
          setLocations(
            res.data.data.map(loc => ({
              label: loc.location_name,
              value: loc.rec_id,
            })),
          );
        }
      } catch (err) {
        console.error('Location fetch failed', err);
      }
    };

    fetchLocations();
  }, []);

  // ───────────────────────────────
  // Submit API
  // ───────────────────────────────
  const submitRequest = async () => {
    let newErrors = {};

    if (!selectedLocation?.value) {
      newErrors.location = 'Location is required';
    }

    if (!filters.startDate) {
      newErrors.startDate = 'Start Date is required';
    }

    if (!filters.endDate) {
      newErrors.endDate = 'End Date is required';
    }

    if (
      filters.startDate &&
      filters.endDate &&
      filters.startDate.isAfter(filters.endDate, 'day')
    ) {
      newErrors.dateRange = 'Start Date cannot be greater than End Date';
    }

    if (!numDevices) {
      newErrors.numDevices = 'Number of devices is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const authToken = JSON.parse(
      localStorage.getItem('headData') || '{}',
    );

    const payload = {
      product_name: 'Attendance',
      start_date: filters.startDate.format('YYYY-MM-DD'),
      end_date: filters.endDate.format('YYYY-MM-DD'),
      location_name: selectedLocation.label,
      location_id: selectedLocation.value,
      noOfDesk: numDevices,
      company_name: UserObj.company_name,
      email: UserObj.email,
      fname: UserObj.fname,
      lname: UserObj.lname,
      phone: UserObj.phone,
    };

    try {
      setLoading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}basic-data/new-product-enquiry`,
        payload,
        {
          headers: {
            Authorization: authToken?.spintlyToken,
          },
        },
      );

      if (res.status === 200) {
        resetForm();
        onClose(true, 'Attendance request submitted successfully');
      }
    } catch (err) {
      toast.error('Unable to submit Attendance request');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedLocation(null);
    setNumDevices('');
    setFilters({ startDate: null, endDate: null });
    setErrors({});
  };

  useImperativeHandle(ref, () => ({
    submitRequest,
    resetForm,
    loading,
  }));

  return (
    <>
      {/* Location */}
      <div className="SwFormGroup">
        <label className="SwFormLabel">
          Location <span className="Required">*</span>
        </label>
        <SwSelect
          value={selectedLocation}
          onChange={val => {
            setSelectedLocation(val);
            setErrors(prev => ({ ...prev, location: '' }));
          }}
          options={locations}
          placeholder="Select Location"
          isClearable
        />
        {errors.location && (
          <span className="SwTextDanger SwTextSmall">{errors.location}</span>
        )}
      </div>

      {/* Dates */}
      <div className="row">
        <div className="col-md-6">
          <div className="mb-3">
            <label className="SwFormLabel">
              Start Date <span className="Required">*</span>
            </label>
            <AntDDatePicker
              value={filters.startDate}
              onChange={val => {
                setFilters(prev => ({ ...prev, startDate: val }));
                setErrors(prev => ({ ...prev, startDate: '', dateRange: '' }));
              }}
              placeholder="Select start date"
              format="DD/MM/YYYY"
              disabledDate={current =>
                filters.endDate && current.isAfter(filters.endDate, 'day')
              }
            />
            {errors.startDate && (
              <span className="SwTextDanger SwTextSmall"> {errors.startDate}</span>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-3">
            <label className="SwFormLabel">
              End Date <span className="Required">*</span>
            </label>
            <AntDDatePicker
              value={filters.endDate}
              onChange={val => {
                setFilters(prev => ({ ...prev, endDate: val }));
                setErrors(prev => ({ ...prev, endDate: '', dateRange: '' }));
              }}
              placeholder="Select end date"
              format="DD/MM/YYYY"
              disabledDate={current =>
                filters.startDate && current.isBefore(filters.startDate, 'day')
              }
            />
            {errors.endDate && (
              <span className="SwTextDanger SwTextSmall">{errors.endDate}</span>
            )}
            {errors.dateRange && (
              <span className="SwTextDanger SwTextSmall">  {errors.dateRange}</span>
            )}
          </div>
        </div>
      </div>

      {/* Number of Devices */}
      <div className="SwFormGroup">
        <label className="SwFormLabel">
          Number of Devices <span className="Required">*</span>
        </label>
        <SwInput
          value={numDevices}
          onChange={e => {
            setNumDevices(e.target.value);
            setErrors(prev => ({ ...prev, numDevices: '' }));
          }}
          onKeyPress={e => {
            if (!/[0-9]/.test(e.key)) e.preventDefault();
          }}
        />
        {errors.numDevices && (
          <span className="SwTextDanger SwTextSmall">{errors.numDevices}</span>
        )}
      </div>
    </>
  );
});

export default AttendanceServiceRequest;
